import React, { useState } from "react";

/**
 * ActionButtons component provides approve/reject actions for registration records
 * Handles the UI for admin actions with confirmation dialogs
 */
const ActionButtons = ({ registration, onApprove, onReject, isLoading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemark, setRejectRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleApprove = async () => {
    if (registration.status !== "pending") return;

    setIsSubmitting(true);
    try {
      await onApprove(registration.id);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectClick = () => {
    if (registration.status !== "pending") return;
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectRemark.trim()) {
      alert("Please provide a reason for rejection");
      return;
    }

    setIsSubmitting(true);
    try {
      await onReject(registration.id, rejectRemark.trim());
      setShowRejectModal(false);
      setRejectRemark("");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectCancel = () => {
    setShowRejectModal(false);
    setRejectRemark("");
  };

  const isPending = registration.status === "pending";
  const isActionLoading = isLoading || isSubmitting;

  return (
    <>
      <div className="action-buttons">
        {isPending ? (
          <>
            <button
              className="approve-btn"
              onClick={handleApprove}
              disabled={isActionLoading}
              type="button"
            >
              {isActionLoading ? "Processing..." : "Approve"}
            </button>
            <button
              className="reject-btn"
              onClick={handleRejectClick}
              disabled={isActionLoading}
              type="button"
            >
              Reject
            </button>
          </>
        ) : (
          <span className="status-text">
            {registration.status === "approved" ? "Approved" : "Rejected"}
            {registration.adminRemark && (
              <span className="admin-remark" title={registration.adminRemark}>
                (with remark)
              </span>
            )}
          </span>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="reject-modal-overlay">
          <div className="reject-modal">
            <div className="reject-modal-header">
              <h3>Reject Registration</h3>
            </div>
            <div className="reject-modal-content">
              <p>
                <strong>User:</strong> {registration.userInfo?.fullName}
              </p>
              <p>
                <strong>Email:</strong> {registration.userInfo?.email}
              </p>
              <div className="reject-reason-field">
                <label htmlFor="rejectRemark">
                  Reason for rejection <span className="required">*</span>
                </label>
                <textarea
                  id="rejectRemark"
                  value={rejectRemark}
                  onChange={(e) => setRejectRemark(e.target.value)}
                  placeholder="Please provide a clear reason for rejecting this registration..."
                  rows={4}
                  className="reject-textarea"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="reject-modal-footer">
              <button
                className="cancel-btn"
                onClick={handleRejectCancel}
                disabled={isSubmitting}
                type="button"
              >
                Cancel
              </button>
              <button
                className="confirm-reject-btn"
                onClick={handleRejectSubmit}
                disabled={isSubmitting || !rejectRemark.trim()}
                type="button"
              >
                {isSubmitting ? "Rejecting..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ActionButtons;
